import { useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { ApplicationState } from 'store/redux';
import { IAuthenticationActionCreators } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import Context from 'configuration/context';
import { authenticationServiceFactory } from 'services/authenticationService';
import { ContactCard } from 'components';
import './Contact.css';

export type IContactProps = IAuthenticationActionCreators & {
  UserState: AuthenticationStore.UserState;
};

const SWITCHBOARD_PHONE = '+46 10-25 26 000';
const HEALTH_CENTER_LINK = 'http://www.avonova.se/om-oss/hitta-till-oss/';

// Customer-administration department to show (computed by the backend)
const CUSTOMER_ADMINISTRATION: Record<string, { name: string; email: string }> = {
  Nationella: { name: 'Avonova Nationellt Kundcenter', email: 'kundcenter@avonova.se' },
  Assist: { name: 'Kundtjänst Mitt Avonova', email: 'kundtjanst@avonova.se' },
  Other: {
    name: 'AKC kund- och konsultadministration',
    email: 'akckundochkonsultadministration@avonova.se',
  },
};

const telHref = (phone: string) => 'tel:' + phone.replace(/[^\d+]/g, '');

const EmailLink = ({ email }: { email: string }) => (
  <a href={'mailto:' + email}>
    <i className="fa fa-envelope"></i>
    {email}
  </a>
);

const PhoneLink = ({ phone }: { phone: string }) => (
  <a href={telHref(phone)}>
    <i className="fa fa-phone"></i>
    {phone}
  </a>
);

const Contact = ({ UserState, setLoginState }: IContactProps) => {
  const { t } = useTranslation('translation');

  const authenticationService = useMemo(
    () => authenticationServiceFactory(Context.APIUrl, { setLoginState }, UserState),
    [UserState]
  );

  const customer = UserState.UserInRole?.selectedCustomer;

  const contactCards = useMemo(() => {
    return customer?.customerContacts
      ? customer.customerContacts.map((contact) => (
          <ContactCard
            className="col-12"
            key={contact.email}
            contact={contact}
            authenticationService={authenticationService}
          />
        ))
      : [];
  }, [customer?.customerContacts]);

  const healthCenter = customer?.responsibleHealthCenter;
  const category = healthCenter?.category ?? 'Other';
  const administration = CUSTOMER_ADMINISTRATION[category] ?? CUSTOMER_ADMINISTRATION.Other;

  return (
    <div>
      <div className="container image-container">
        <div className="row limited">
          <img className="col-12 contact-hero" src="/assets/images/hero-contact.jpg" alt="" />
        </div>
      </div>
      <div className="container contact-page">
        <div className="row limited">
          <div className="col-12 col-lg-6 mt-4">
            <section className="contact-block">
              <h3 className="contact-block-heading">{t('contact.CONTACT_US_HEADING')}</h3>
              <p className="contact-block-subheading">{t('contact.CONTACT_US_SUBHEADING')}</p>
              <div className="contact-block-name">{administration.name}</div>
              <div className="contact-block-line">
                <EmailLink email={administration.email} />
              </div>
              <div className="contact-block-line">
                <PhoneLink phone={SWITCHBOARD_PHONE} />
              </div>
            </section>

            <section className="contact-block">
              <h3 className="contact-block-heading">{t('contact.ORDER_HEADING')}</h3>
              {category === 'Other' && healthCenter?.name ? (
                <>
                  <p className="contact-block-subheading">{t('contact.ORDER_SUBHEADING')}</p>
                  <div className="contact-block-name">{healthCenter.name}</div>
                  {healthCenter.email && (
                    <div className="contact-block-line">
                      <EmailLink email={healthCenter.email} />
                    </div>
                  )}
                  <div className="contact-block-line">
                    <PhoneLink phone={SWITCHBOARD_PHONE} />
                  </div>
                </>
              ) : (
                <>
                  <p className="contact-block-subheading">{t('contact.INFO_OTHER_ORDER')}</p>
                  <p className="contact-block-subheading">
                    <Trans i18nKey="contact.INFO_OTHER_CONTACT_POSSIBILITIES" ns="translation">
                      <a target="_blank" rel="noreferrer" href={HEALTH_CENTER_LINK}></a>
                    </Trans>
                  </p>
                </>
              )}
            </section>
          </div>

          {contactCards.length > 0 && (
            <div className="col-12 col-lg-6 mt-4">
              <section className="contact-block">
                <h3 className="contact-block-heading">{t('contact.ACCOUNT_MANAGER_HEADING')}</h3>
                <p className="contact-block-subheading">{t('contact.ACCOUNT_MANAGER_SUBHEADING')}</p>
                <div className="row">{contactCards}</div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default connect((appState: ApplicationState) => ({ UserState: appState.authentication }), {
  ...AuthenticationStore.actions,
})(Contact);
